import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";

// Real-audio "Worship Radio" — unlike the YouTube-embed gospel catalog
// (which YouTube pauses when backgrounded), these tracks stream from a
// real <audio> element with HTTP Range support, so playback CONTINUES
// with the screen off and shows lock-screen controls (MediaSession).
// The <audio> element lives at the app root so it survives navigation.
const Ctx = createContext(null);

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export function WorshipRadioProvider({ children }) {
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const indexRef = useRef(0);
  const tracksRef = useRef([]);
  const shuffleRef = useRef(false);

  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  const toggleShuffle = useCallback(() => setShuffle((s) => !s), []);

  const ensureEl = useCallback(() => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.addEventListener("play", () => setIsPlaying(true));
      el.addEventListener("playing", () => setIsPlaying(true));
      el.addEventListener("pause", () => setIsPlaying(false));
      audioRef.current = el;
    }
    return audioRef.current;
  }, []);

  const srcFor = (t) => (t ? `${BACKEND}${t.url}` : "");

  const playIndex = useCallback((i) => {
    const list = tracksRef.current;
    if (!list.length) return;
    const idx = ((i % list.length) + list.length) % list.length;
    const el = ensureEl();
    // Silence any other audio (Bible read-aloud + YouTube music) so
    // voices never overlap. We DON'T fire hw:bgmusic:start (we listen
    // to it ourselves to pause), so use the read-aloud + audio buses.
    try { window.dispatchEvent(new CustomEvent("hw:audio:start")); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "worship-radio" } })); } catch (_) {}
    setIndex(idx);
    el.src = srcFor(list[idx]);
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [ensureEl]);

  const start = useCallback(async (startIndex = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get("/background-music");
      const list = data?.tracks || [];
      if (!list.length) { setLoading(false); return { empty: true }; }
      setTracks(list);
      tracksRef.current = list;
      // playIndex reads tracksRef, which we just set synchronously.
      playIndex(Math.min(startIndex, list.length - 1));
      setLoading(false);
      return { empty: false };
    } catch (_) {
      setLoading(false);
      return { empty: true, error: true };
    }
  }, [playIndex]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !tracksRef.current.length) { start(); return; }
    if (el.paused) {
      try { window.dispatchEvent(new CustomEvent("hw:audio:start")); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "worship-radio" } })); } catch (_) {}
      const p = el.play(); if (p && p.catch) p.catch(() => {});
    } else {
      el.pause();
    }
  }, [start]);

  const next = useCallback(() => playIndex(indexRef.current + 1), [playIndex]);
  const prev = useCallback(() => playIndex(indexRef.current - 1), [playIndex]);

  // Advance to the next track — random pick when shuffle is on.
  const advance = useCallback(() => {
    const len = tracksRef.current.length;
    if (len <= 1) { playIndex(indexRef.current + 1); return; }
    if (shuffleRef.current) {
      let r = indexRef.current;
      while (r === indexRef.current) r = Math.floor(Math.random() * len);
      playIndex(r);
    } else {
      playIndex(indexRef.current + 1);
    }
  }, [playIndex]);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (el) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) {} }
    setIsPlaying(false);
    setTracks([]);
    tracksRef.current = [];
    setIndex(0);
  }, []);

  // Auto-advance when a track ends.
  useEffect(() => {
    const el = ensureEl();
    const onEnded = () => advance();
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [ensureEl, advance]);

  // Pause ourselves when another audio source starts (Bible read-aloud
  // from a different surface, or the YouTube gospel music) so nothing
  // overlaps. We ignore our own read-aloud id.
  useEffect(() => {
    const onReadAloud = (e) => {
      if (e?.detail?.id === "worship-radio") return;
      const el = audioRef.current;
      if (el && !el.paused) el.pause();
    };
    const onBg = () => {
      const el = audioRef.current;
      if (el && !el.paused) el.pause();
    };
    window.addEventListener("hw:read-aloud:start", onReadAloud);
    window.addEventListener("hw:bgmusic:start", onBg);
    return () => {
      window.removeEventListener("hw:read-aloud:start", onReadAloud);
      window.removeEventListener("hw:bgmusic:start", onBg);
    };
  }, []);

  // MediaSession — lock-screen / notification controls that keep the
  // stream alive with the screen off.
  const current = tracks[index] || null;
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    if (!current) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: current.title || "Worship",
        artist: current.artist || "Holy Words",
        album: "Holy Words · Radio",
        artwork: current.art
          ? [{ src: current.art, sizes: "512x512", type: "image/png" }]
          : [
              { src: "/icons/holywords-192.png", sizes: "192x192", type: "image/png" },
              { src: "/icons/holywords-512.png", sizes: "512x512", type: "image/png" },
            ],
      });
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch (_) {}
    const handlers = {
      play: () => toggle(),
      pause: () => toggle(),
      nexttrack: () => next(),
      previoustrack: () => prev(),
    };
    Object.entries(handlers).forEach(([a, fn]) => {
      try { navigator.mediaSession.setActionHandler(a, fn); } catch (_) {}
    });
    return () => {
      Object.keys(handlers).forEach((a) => {
        try { navigator.mediaSession.setActionHandler(a, null); } catch (_) {}
      });
    };
  }, [current, isPlaying, toggle, next, prev]);

  return (
    <Ctx.Provider value={{
      tracks, index, current, isPlaying, loading, shuffle,
      start, toggle, next, prev, stop, toggleShuffle,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useWorshipRadio() {
  return useContext(Ctx);
}
